import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useClientBranding } from '../contexts/ClientBrandingContext';
import { partnerAuthName } from '../utils/clientInvite';

/**
 * SEOHead Component
 * Manages all SEO-related meta tags for the page
 * 
 * @param {string} title - Page title (will be appended with site name)
 * @param {string} description - Meta description (max 160 chars recommended)
 * @param {string} keywords - Comma-separated keywords
 * @param {string} canonical - Canonical URL for the page
 * @param {string} ogImage - Open Graph image path
 * @param {string} ogType - Open Graph type (website, article, product)
 * @param {object|array} structuredData - JSON-LD structured data (single object or array)
 * @param {boolean} noIndex - Set to true to prevent indexing
 * @param {string} locale - Page locale (default: en_US)
 * @param {string} publishedTime - Article published time (ISO 8601)
 * @param {string} modifiedTime - Article modified time (ISO 8601)
 */
const SEOHead = ({ 
  title = "Déjà Vu Nomad - Professional Travel Planning App",
  description = "Plan perfect trips with friends and family. Collaborative trip planning made easy with real-time chat, smart itineraries, and group suggestions.",
  keywords = "travel planning, group travel, trip planner, collaborative planning, travel itinerary, travel app, vacation packages, travel deals",
  canonical,
  ogImage = "/og-card.png",
  ogType = "website",
  structuredData = null,
  noIndex = false,
  locale = "en_US",
  publishedTime = null,
  modifiedTime = null,
  siteName: siteNameProp
}) => {
  const { pathname } = useLocation();
  const { branding } = useClientBranding();
  const onPartnerOrAdmin = pathname.startsWith('/partner') || pathname.startsWith('/admin');
  const siteName = siteNameProp
    || (!onPartnerOrAdmin && partnerAuthName(branding))
    || 'Déjà Vu Nomad';
  // Ensure title includes brand name
  const fullTitle = title.includes(siteName) || title.includes('Déjà Vu Nomad')
    ? title
    : `${title} | ${siteName}`;
  
  // Ensure description is within limits
  const safeDescription = description.length > 160 
    ? description.substring(0, 157) + '...' 
    : description;
  
  // Build full image URL
  const fullImageUrl = ogImage.startsWith('http') 
    ? ogImage 
    : `https://dejavunomad.com${ogImage}`;
  
  // Self-canonical for the current route when a page omits `canonical`.
  // Never default to the homepage — that tells Google every URL is a duplicate of /.
  const implicitCanonical = pathname === '/'
    ? 'https://dejavunomad.com/'
    : `https://dejavunomad.com${pathname}`;

  // Build and normalize canonical URL (always https://dejavunomad.com, no www)
  const normalizeUrl = (url) => {
    if (!url.startsWith('http')) return `https://dejavunomad.com${url}`;
    return url
      .replace(/^http:\/\//, 'https://')
      .replace('://www.dejavunomad.com', '://dejavunomad.com');
  };
  const fullCanonical = normalizeUrl(canonical || implicitCanonical);

  // Handle structured data (can be single object or array)
  const renderStructuredData = () => {
    if (!structuredData) return null;
    
    const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
    
    return dataArray.map((data, index) => (
      <script key={index} type="application/ld+json">
        {JSON.stringify(data)}
      </script>
    ));
  };
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={safeDescription} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullCanonical} />
      
      {/* Robots */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={safeDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:creator" content="@DejaVuNomadApp" />
      <meta name="twitter:site" content="@DejaVuNomadApp" />
      
      {/* Article Meta (for blog posts, news) */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content={siteName} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      <meta name="theme-color" content="#8B1538" />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      
      {/* Structured Data */}
      {renderStructuredData()}
    </Helmet>
  );
};

export default SEOHead;
